import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/api/client";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Upload, Trash2, ImageOff } from "lucide-react";
import { toast } from "sonner";

export default function GalleryPage() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchItems = async () => {
    const res = await api.get("/gallery");
    setItems(res.data || []);
  };

  useEffect(() => { fetchItems(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const closeDialog = () => {
    setOpen(false);
    setFile(null);
    setPreview(null);
    setTitle("");
    setDescription("");
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a photo first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("description", description);
      await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      closeDialog();
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/gallery/${deleteId}`);
      toast.success("Deleted");
      setDeleteId(null);
      fetchItems();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="gallery-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{t.portal.nav.gallery}</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} photos • visible on landing page</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="gallery-upload-btn">
          <Plus className="h-4 w-4 mr-1" /> Upload photo
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">
          <ImageOff className="h-8 w-8 mx-auto opacity-60 mb-3" />
          No photos yet. Upload your first job photo.
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((item) => (
            <div key={item.id} className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-card" data-testid={`gallery-tile-${item.id}`}>
              <img
                src={`${API_BASE}/gallery/file/${item.id}`}
                alt={item.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/85 to-transparent">
                <div className="text-xs font-semibold text-white truncate">{item.title || item.original_filename}</div>
              </div>
              <button
                onClick={() => setDeleteId(item.id)}
                className="absolute top-2 right-2 h-8 w-8 rounded-full bg-destructive/90 hover:bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete"
                data-testid={`gallery-delete-${item.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="gallery-upload-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: "Outfit" }}>Upload Job Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {preview ? (
              <div className="rounded-lg border border-border overflow-hidden aspect-video bg-background">
                <img src={preview} alt="preview" className="w-full h-full object-contain" />
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border p-10 cursor-pointer hover:border-primary transition-colors">
                <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                <div className="text-sm text-muted-foreground">Click to pick a photo</div>
                <div className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP up to 10MB</div>
                <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="gallery-file-input" />
              </label>
            )}
            {preview && (
              <button onClick={() => { setFile(null); setPreview(null); }} className="text-xs text-muted-foreground underline">
                Choose a different photo
              </button>
            )}
            <Input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-background" data-testid="gallery-title-input" />
            <Textarea placeholder="Description (optional)" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="bg-background" />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={closeDialog} className="rounded-full">Cancel</Button>
            <Button disabled={!file || uploading} onClick={upload} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="gallery-upload-submit">
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
            <AlertDialogDescription>It will no longer appear in the public gallery.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground" data-testid="gallery-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
